import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Keeps one broken part of a page from blanking the rest of it.
 *
 * Without a boundary React unmounts the whole tree on an uncaught render
 * error: an agent mid-call would be left looking at an empty window. This
 * catches it where it is placed and shows `fallback` instead, with a way to
 * try again; `resetKey` clears it on its own (a new page, a new call).
 */

interface Props {
  fallback: (error: Error, reset: () => void) => ReactNode;
  resetKey?: unknown;
  children: ReactNode;
}

export class ErrorBoundary extends Component<Props, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The browser console is where a supporter will look; say where it broke.
    console.error('[bridge] a part of the page failed to render', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    return this.state.error ? this.props.fallback(this.state.error, this.reset) : this.props.children;
  }
}
